import { ASEAN_MEMBER, AVAILABLE_YEARS, HIGHCHART_COLORS } from "../../../../libs/constant";

import React,{FC, Suspense, useEffect, useState, useMemo} from "react";
import Highcharts from 'highcharts'
import { Row, Spinner, Modal, Button, Col, Form, Pagination } from "react-bootstrap";
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, RowData, SortingState, useReactTable} from '@tanstack/react-table';
import * as _ from 'lodash'
import { getNameByISO3, shortenLargeNumber } from "../../../../libs/misc";
import { TradeDataTable } from "../core/_models";
import ChartWithShare from "../../../../../ui-kit/ChartWithShare";

type CommodityDetailModalProps = {
  params:any;
  handleDisplayToggle:()=>void;
  product_group?: any;
}


interface ExtendedHighchartPiePoint{
  key:string,
  x: number,
  y: number,
  percentage: number
  name:string,
  trade_value: number,
}
interface ExtendedHighchartCreditInterface{
  credits:any
}

const columnHelper = createColumnHelper<TradeDataTable>()

const CommodityDetailModal:FC<CommodityDetailModalProps> = ({
  params,
  handleDisplayToggle,
  product_group = [],
}) => {
  const highchartOptions = {
    chart:{
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: 'pie',
      marginTop: 0,
      events: {
        load: function (this:ExtendedHighchartCreditInterface) {
          this.credits.element.onclick = function () { window.open('http://comtrade.un.org', '_blank'); }
        }
      }
    },
    tooltip: {
      headerFormat: '',
      pointFormatter: function () {}
    },
    title:{
      text:''
    },
    series: [{
      data: [1,2,3]
    }],
    plotOptions: {
      pie: {
        size: '60%',
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          formatter: function (this:ExtendedHighchartPiePoint) {
            const {key, percentage} = this
            const dataLabel = (percentage < 0.01) ? '<0.01' : percentage.toFixed(2);
            return `${key}: ${dataLabel}%`;
          },
          style: {
            color:  ((Highcharts as any).theme?.contrastTextColor) || 'black',
            fontWeight: 'normal'
          },
          // connectorColor: 'silver'
        }
      }
    },
    credits:{
      enabled: true,
      text: `Data source: UN Comtrade`,
      href: '#',
      position: {
        align: 'center'
      }
    },
    exporting: {
      enabled: true
    },
    accessibility: {
      enabled: false
    }
  };
  const columns = [
      columnHelper.accessor('hs',{
          header: 'HS Code',
          cell: info=>{
            const flow = info.row.original.flow
            const hs = info.getValue()
            return (
              <span 
                className="badge cursor-pointer text-primary fw-1000 " 
                style={{background:'rgba(5, 66, 148, 0.1)'}}>
                {hs}
              </span>
              )
            }
        }),
        columnHelper.accessor('description',{
          header: 'Description',
          cell: info=>info.getValue()
        }),
        columnHelper.accessor('val',{
          header: "Value",
          cell: info=>shortenLargeNumber(info.getValue())
        })
    ];
  const [worldTradeValue, setWorldTradeValue] = useState(0)
  const [pieChartOptions, setPieChatOptions] = useState<any>(highchartOptions)
  const [dataTable, setDataTable] = useState<TradeDataTable[]>()

  // Build dynamic labels and titles using useMemo for stability and performance
  const [mainTitle, subTitle] = useMemo(() => {
    const findPG = (id: number, list: any[]): any | null => {
      for (const item of list) {
        if (item.id === id) return item;
        if (item.sub_category?.length) {
          const found = findPG(id, item.sub_category);
          if (found) return found;
        }
      }
      return null;
    };

    let pgs = params.product_group || []
    if (!Array.isArray(pgs)) pgs = [pgs]
    let pgLabel = ''
    if (pgs.length === 1) {
      const selectedPG = findPG(+pgs[0], product_group);
      pgLabel = selectedPG?.sub_category?.length ? `${selectedPG.name} (Detail)` : (selectedPG?.name || 'Product Group')
    } else {
      const names = pgs.map((id: any) => findPG(+id, product_group)?.name || '').filter(Boolean)
      pgLabel = names.length > 0 ? names.join(', ') : 'Product Group'
    }

    const year = params.year || (params.commodityData?.rows?.[0]?.year) || ''
    const flow = params.tradeflow === 'imports' ? 'Import' : 'Export'

    if (params.type === 'chart') {
      const main = `Top ${params.tradeflow} partners of commodity ${params.commodity}`
      const sub = `${params.description} - ${pgLabel} in ${year}`
      return [main, sub]
    } else {
      const partnerName = getNameByISO3(params.partner)
      const action = params.tradeflow === 'exports' ? `exported to` : `imported from`
      const main = `List of commodities ${action} ${partnerName}`
      const sub = `${pgLabel} in ${year}`
      return [main, sub]
    }
  }, [params, product_group])

  const cardTitle = `${mainTitle} - ${subTitle}`;
  const [pagination, setPagination] = React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 20,
    })
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const tradeTable = useReactTable({
      data:dataTable || [],
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      onGlobalFilterChange: setGlobalFilter,
      onSortingChange: setSorting,
      state: {
        pagination,
        globalFilter,
        sorting
      },
  })
  const generatePieChartTradeDataByCommodity = () =>{
    const tradeData = params.commodityData.rows
    const tradeDescription = params.description
    const tradeflow = params.tradeflow==='imports'?1:2
    if(tradeData.length<1){
      throw Error("Error")
    }    
    const worldValue = tradeData.filter((trade)=>trade.par==='WLD' && trade.flow===tradeflow)
    setWorldTradeValue(worldValue[0].val)

    //Accumulate where reporter is ASEAN
    let tradeDataFiltered = _.chain(tradeData)
      .filter(item => item.par !== 'WLD')
      .groupBy(item => `${item.par}`)
      .map((group, key) => {
        const par = key;
        return {
          rep: 'ASEAN',
          par,
          qty: _.sumBy(group, 'qty'),
          val: _.sumBy(group, 'val')
        };
      })
      .value();
    //Accumulate where partner is ASEAN Member for drilldown purposes
    let graph_dataset_drilldown = tradeDataFiltered.filter((item)=>ASEAN_MEMBER.includes(item.par))

    const total_drilldown_trade_value = graph_dataset_drilldown.reduce((acc,item)=>acc+=item.val,0)

    graph_dataset_drilldown = graph_dataset_drilldown.map((item,i)=>{
      const percentage = Number(item.val/total_drilldown_trade_value).toFixed(4)
      return {
        name: getNameByISO3(item.par),
        iso3: item.par,
        color: Highcharts.getOptions().colors?.[i] || HIGHCHART_COLORS[i],
        trade_value: item.val,
        y: Number(percentage),
      }
    }).sort((a, b) => b.trade_value - a.trade_value);

    graph_dataset_drilldown = {
        name: 'ASEAN',
        id: 'ASEAN',
        data: [...graph_dataset_drilldown],
        point: {
            events: {
              click(e) {
                const rep = worldValue[0].rep
                const hs = worldValue[0].hs.length===1 && worldValue[0].hs[0].length===6?worldValue[0].hs[0]:null
                if(hs.length===6 && rep.length===3){
                  const combination = params.tradeflow === 'imports' ? `${e.point.iso3}-${rep}` : `${rep}-${e.point.iso3}`
                  const tinaCommodityURL = `${process.env.REACT_APP_TINA_BASE_URL}/app/dashboard/${combination}/commodity/${hs}?ref=home&tradeflow=${params.tradeflow}` 
                  window.open(tinaCommodityURL, "_blank");
                }
              }
            }
          }
      }

    const consolidateData = [
      ...tradeDataFiltered.filter(item=>!ASEAN_MEMBER.includes(item.par)),
      {
        rep:'ASEAN',
        par:'ASEAN',
        // flow:1,
        val: total_drilldown_trade_value
      }
    ]
    const tradeDataSorted = _.chain(consolidateData)
                            .orderBy('val','desc')
                            .value()

    const topData = tradeDataSorted.map(trade=>{
      return{
        ...trade,
        percentage: ((trade.val/worldValue[0].val)* 100).toFixed(4)
      }
    })
    const graphDataset = [
      ...topData.map((trade, idx)=>{
        if(trade.par==='ASEAN'){
          return {
            name: getNameByISO3(trade.par),
            y: +trade.percentage,
            trade_value: trade.val,
            color: HIGHCHART_COLORS[idx],
            iso3: trade.par,
            tradeflow: trade.flow,
            drilldown:'ASEAN'
          }
        }
        return {
          name: getNameByISO3(trade.par),
          y: +trade.percentage,
          trade_value: trade.val,
          iso3: trade.par,
          color: HIGHCHART_COLORS[idx]
        }
      })
    ]
    setPieChatOptions({
      ...highchartOptions,
      // title:{
      //   text:`Commodity: ${tradeData.hs_code} - ${tradeDescription}`,
      //   align:'left'
      // },
      series: [{
        data: graphDataset,
        point:{
          events:{
            click(e){
              if (e.point.destroyed) {
                  console.log('Drilldown available, no action taken.');
              } else {
                const rep = worldValue[0].rep
                const hs = worldValue[0].hs.length===1 && worldValue[0].hs[0].length===6?worldValue[0].hs[0]:null
                if(hs.length===6 && rep.length===3){
                  const combination = params.tradeflow === 'imports' ? `${e.point.iso3}-${rep}` : `${rep}-${e.point.iso3}`
                  const tinaCommodityURL = `${process.env.REACT_APP_TINA_BASE_URL}/app/dashboard/${combination}/commodity/${hs}?ref=home&tradeflow=${params.tradeflow}` 
                  window.open(tinaCommodityURL, "_blank");
                }
              }
            }
          }
        }
      }],
      drilldown:{
        series: [graph_dataset_drilldown]
      },
      tooltip:{
        headerFormat: '',
        pointFormatter: function (this:ExtendedHighchartPiePoint) {
          const {name, percentage, trade_value, key} = this
          if (name === 'Others') {
            return `
            <strong>${key}</strong>
            <br />Share of Imports: <strong>${percentage.toFixed(2)}%</strong>`;
          } else {
            var formattedPercentage = (percentage < 0.01) ? '<0.01' : percentage.toFixed(2);
            return `
              <strong>${name}</strong>
              <br />Share of Imports: <strong>${formattedPercentage}%</strong>
              <br />Value of Imports: US$ ${shortenLargeNumber(trade_value)}`;
          }
        }
      }
    })
    // Title handled by useMemo
    const pgLabel = buildProductGroupLabel()
    // setCardTitle(...) removed
  }

  const generateTableTradeDataByPartner = () =>{
    setDataTable(params.commodityData.rows.map((trade)=>{
      return {
        hs: trade.hs,
        description: trade.description,
        val: trade.total_value
      }
    }))
    // Title handled by useMemo
    const pgLabel = buildProductGroupLabel()
  }

  const buildProductGroupLabel = () => {
    let selectedPGs = params.product_group || []
    if(!Array.isArray(selectedPGs)){
      selectedPGs = [selectedPGs]
    }
    if(selectedPGs.length === 1){
      const selectedPG = product_group.find((pg:any) => pg.id === +selectedPGs[0])
      if(selectedPG?.sub_category?.length){
        return `${selectedPG.name} (Detail)`
      }
      return selectedPG?.name || 'Product Group'
    }
    const selectedNames = selectedPGs.map((pgId: any) => {
      const found = product_group.find((pg: any) => pg.id === +pgId)
      return found?.name || ''
    }).filter(Boolean)
    return selectedNames.length > 0 ? selectedNames.join(', ') : 'Product Group'
  }

  useEffect(()=>{
    setDataTable([])
    if(params.commodityData && params.commodityData.rows?.length>0){
      if(params.type==='chart'){
        generatePieChartTradeDataByCommodity()
      }else if(params.type==='table'){
        generateTableTradeDataByPartner()
      }
    }
  },[params])

  const pagesTable = paginationRange(tradeTable);

  function paginationRange(dataTable) {
    const currentPage = pagination.pageIndex + 1;
    const totalPages = dataTable.getPageCount();
    const maxPagesToShow = 1;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range:any = [];

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }

    return range;
  }

  const handlePageChange = (page, dataTable) => {
    const currentPage = pagination.pageIndex+1
    const totalPages = dataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      dataTable.setPageIndex(page-1)
    }
  };

  const handleDisplayPageChange = (e) =>{
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value
    })
  }

  const modalBodyContent = () => {
    switch (params.type) {
      case 'chart':
        // return <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />;
        return <ChartWithShare chartOptions={pieChartOptions} title={cardTitle} />;
      case 'table':
        return (
          <div className="fade-in">
            <div className='table-responsive table-fixhead'>
              <table className='table table-row-bordered table-hover align-middle gs-0'>
                <thead>
                  {tradeTable.getHeaderGroups().map(headerGroup => (
                    <tr className='fw-bold text-gray-800 py-4' key={headerGroup.id}>
                      <th className='ps-4 min-w-75px bg-light rounded-start' >HS Code</th>
                      <th className='min-w-125px bg-light'>Description</th>
                      <th className='min-w-90px text-end rounded-end px-3 bg-light'>Value</th>
                    </tr>
                  ))}
                </thead>
                <tbody className="gy-1 overflow-auto">
                  {tradeTable.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        <td key={row.getVisibleCells()[0].id} className="text-center">
                          {flexRender(
                            row.getVisibleCells()[0].column.columnDef.cell, 
                            row.getVisibleCells()[0].getContext())
                          }
                        </td>
                        <td key={row.getVisibleCells()[1].id} className="text-start">
                          {flexRender(
                            row.getVisibleCells()[1].column.columnDef.cell, 
                            row.getVisibleCells()[1].getContext())
                          }
                        </td>
                        <td key={row.getVisibleCells()[2].id} className="text-end fw-bold px-5">
                          {flexRender(
                            row.getVisibleCells()[2].column.columnDef.cell, 
                            row.getVisibleCells()[2].getContext())
                          }
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Row className='my-5' style={{ fontSize: '0.8em' }}>
              <Col md={4} className='d-flex justify-content-start align-items-center'>
                Display
                <Form.Select
                  size="sm"
                  className="w-80px mx-2"
                  onChange={handleDisplayPageChange}
                  value={pagination.pageSize}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Select>
                results
              </Col>
              <Col md={8} className='d-flex justify-content-end'>
                <Pagination size="sm">
                  <Pagination.Prev
                    onClick={() => tradeTable.previousPage()}
                    disabled={!tradeTable.getCanPreviousPage()} />
                  {/* Start Dynamic Page */}
                  {pagesTable.map((page, index) => {
                    if (page === '...') {
                      return (
                        <Pagination.Ellipsis key={index} />
                      );
                    }
                    return (
                      <Pagination.Item
                        key={index}
                        active={page === pagination.pageIndex + 1}
                        onClick={() => handlePageChange(page, tradeTable)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}
                  {/* End Dynamic Page */}
                  <Pagination.Next
                    onClick={() => tradeTable.nextPage()}
                    disabled={!tradeTable.getCanNextPage()} />
                </Pagination>
              </Col>
            </Row>
          </div>
          
        );
      default:
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height:'300px'}}>
            <Spinner animation="border" variant="primary" className="me-3"/> Loading ...
          </div>
        );
    }
  };

  const commoditySubtitle = () => {
    switch(params.type){
      case 'chart':
        return (
          <Row className="mb-3">
            <small>{`Commodity: ${params.description}`}</small>
          </Row>
        )
      default: return null
    }
  }

  return (
    <Modal 
      show={params.show} onHide={handleDisplayToggle} size="lg" centered>
      <Modal.Header closeButton className="d-flex">
        <Modal.Title
          title={cardTitle}
          style={{
            maxWidth: '100%',
          }}
        >
          <div className="d-flex flex-column">
            <span className="fw-bold">{mainTitle}</span>
            <span className="text-muted fs-7 fw-normal mt-1">{subTitle}</span>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{minHeight:'300px'}}>
        {commoditySubtitle()}
        <Row>
          <Suspense fallback={<Spinner animation="border" variant="primary" />}>
            {modalBodyContent()}
          </Suspense>
        </Row>        
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline" onClick={handleDisplayToggle}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export {CommodityDetailModal}